import React, {useMemo, useState} from 'react';
import style from './style.module.scss';
import {Typography} from 'components/Typography';
import cs from 'classnames';
import {Tooltip} from 'components/Tooltip';
import CustomRoutingLink from 'components/CustomRoutingLink';
import {MobileModal} from 'modules/modals/components/MobileModal';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import {useIsTabletByScreen} from 'core/hooks/useIsTabletByScreen';
import Icon from 'components/Icon';

interface ICashbackLabelProps {
    value?: number | null;
    className?: string;
    withTooltip?: boolean;
}

export const CashbackLabel = ({value = 100, className, withTooltip}: ICashbackLabelProps) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const [isTabletTooltipOpen, setTabletTooltipOpen] = useState(false);
    const isTablet = useIsTabletByScreen();
    const isMobile = useIsMobileByScreen();

    const content = useMemo(
        () => (
            <>
                <div className={style.tooltipTitle}>
                    <Icon className={cs(style.icon, style.tooltipIcon)} icon={'snippet_bonuses'} />
                    <Typography
                        className={cs(style.text)}
                        variants={{DT: 'label-14-m', TB: 'label-14-m', MB: 'title-20-m'}}
                    >
                        Больше бонусов!
                    </Typography>
                </div>
                <Typography
                    variants={{DT: 'label-12-r', TB: 'label-12-r', MB: 'body-16-r'}}
                    className={style.tooltipText}
                >
                    За товар с таким значком вы получите увеличенное количество бонусов. Напомним, что
                    1&nbsp;бонус&nbsp;=&nbsp;1&nbsp;рубль, а&nbsp;начисление повышенного бонуса произойдет в течение
                    двух недель после совершения покупки. За&nbsp;остальные&nbsp;товары&nbsp;мы тоже начисляем бонусы в
                    размере 5% от&nbsp;стоимости товара.
                </Typography>
                <div>
                    <CustomRoutingLink href={'/cashback/'} className={style.moreButton}>
                        <Typography variants={{DT: 'label-12-r', TB: 'label-12-r', MB: 'body-16-r'}} color={'gray60'}>
                            Подробнее
                        </Typography>
                    </CustomRoutingLink>
                </div>
            </>
        ),
        [],
    );

    const tooltipAdditionalProps = useMemo(
        () => ({
            overlay: <div className={style.tooltipContainer}>{content}</div>,
            closeOnScroll: true,
            onClick: () => {
                if (isMobile) {
                    setModalOpen(true);
                }
            },
            ...(isTablet
                ? {
                      setIsOpen: (isOpen: boolean) => {
                          setTabletTooltipOpen(isOpen);
                      },
                      openOnClick: true,
                      isOpen: isTabletTooltipOpen,
                  }
                : {}),
            ...(isMobile
                ? {
                      openOnClick: true,
                      isOpen: false,
                  }
                : {}),
        }),
        [content, isMobile, isTablet, isTabletTooltipOpen],
    );

    if (!value) {
        return null;
    }

    return (
        <>
            <Tooltip
                {...tooltipAdditionalProps}
                place={'top-end'}
                childrenClassName={cs(className, style.main, {
                    [style.isClickable]: Boolean(withTooltip),
                })}
            >
                <>
                    <Icon className={style.icon} icon={'snippet_bonuses'} />
                    <Typography className={cs(style.text)} variant={'label-12-m'}>
                        {value} бонусов
                    </Typography>
                </>
            </Tooltip>
            {isModalOpen && (
                <MobileModal
                    className={style.modalContent}
                    classNames={{closeButton: style.modalCloseButton}}
                    onClose={() => {
                        setModalOpen(false);
                    }}
                >
                    {content}
                </MobileModal>
            )}
        </>
    );
};
